import { ThemeEvents, CartUpdatedEvent } from '@theme/events';

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.checkoutButton = null;
    this.openCartIcons = null;
    this.renderSequence = 0;
    this.closeTimer = null;
    this.returnFocus = null;
    this.onKeyDown = (event) => {
      if (!this.isOpen()) return;
      if (event.key === 'Escape') { event.preventDefault(); this.close(); }
      if (event.key !== 'Tab') return;
      const controls = [...this.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),[tabindex="0"]')].filter(el => el.getClientRects().length);
      if (!controls.length) return;
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
  }

  connectedCallback() {
    this.checkoutButton = this.querySelector('[data-ref="checkout-button"]');

    this.addEventListener('click', (event) => this.#onBackdropClick(event));
    this.addEventListener('keydown', this.onKeyDown);

    this.checkoutButton?.addEventListener('click', (event) => this.#handleCheckoutButtonClick(event));

    document.addEventListener(ThemeEvents.cartUpdated, (event) => this.#handleCartUpdate(event));

    document.addEventListener(ThemeEvents.cartOpen, () => this.open());
    document.addEventListener(ThemeEvents.cartClose, () => this.close());
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartClose, () => this.close());
    document.removeEventListener(ThemeEvents.cartOpen, () => this.open());

    document.removeEventListener(ThemeEvents.cartUpdated, (event) => this.#handleCartUpdate(event));

    this.checkoutButton?.removeEventListener('click', (event) => this.#handleCheckoutButtonClick(event));

    this.removeEventListener('click', (event) => this.#onBackdropClick(event));
  }

  #handleCheckoutButtonClick(event) {
    event.target.classList.add('is-loading');
  }

  #handleCartUpdate(event) {
    this.renderCartContents(event.detail.data);
  }

  #onBackdropClick(event) {
    const rect = this.querySelector('.cart-drawer__inner').getBoundingClientRect();
    const isInDialog = rect.top <= event.clientY && event.clientY <= rect.top + rect.height && rect.left <= event.clientX && event.clientX <= rect.left + rect.width;

    if (!isInDialog) {
      this.close();
    }
  }

  open() {
    clearTimeout(this.closeTimer);
    this.returnFocus = document.activeElement;
    // Nettoyer les classes pour éviter les conflits
    this.classList.remove('is-closing');
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    this.querySelector('.cart-drawer__inner')?.focus({ preventScroll: true });
  }

  close() {
    if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
    // Ajouter la classe de fermeture pour déclencher l'animation
    this.classList.add('is-closing');

    // Attendre la fin de l'animation avant de masquer complètement
    this.closeTimer = setTimeout(() => {
      this.classList.remove('is-open', 'is-closing');
      document.body.classList.remove('overflow-hidden');
    }, 125); // Durée légèrement inférieure à --animation-speed pour éviter les décalages
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  async renderCartContents(parsedState = {}) {
    const sequence = ++this.renderSequence;
    const sectionId = this.dataset.sectionId;
    this.setAttribute('aria-busy', 'true');
    try {
      let html = parsedState.sections?.[sectionId];
      if (!html) {
        const endpoint = new URL(window.location.href);
        endpoint.searchParams.delete('sections');
        endpoint.searchParams.set('section_id', sectionId);
        const response = await fetch(endpoint, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Cart section unavailable');
        html = await response.text();
      }
      if (sequence !== this.renderSequence) return;
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const replacement = documentFragment.querySelector('.cart-drawer__inner');
      if (!replacement) throw new Error('Cart section missing');
      const inner = this.querySelector('.cart-drawer__inner');
      inner.innerHTML = replacement.innerHTML;
      this.classList.toggle('cart-drawer--empty', !!inner.querySelector('.pdu--empty'));
      const count = [...inner.querySelectorAll('[data-ref="quantity-selector-input"]')].reduce((sum, input) => sum + Number(input.value || 0), 0);
      document.querySelectorAll('cart-icon [data-ref="cart-count"]').forEach(el => { el.textContent = String(count); });
      this.querySelector('[data-ref="checkout-button"]')?.addEventListener('click', (event) => this.#handleCheckoutButtonClick(event));
    } catch (error) {
      if (sequence !== this.renderSequence) return;
      // A failed drawer must never block access to Shopify's actual cart.
      window.location.assign(Theme.routes.cart_url);
    } finally {
      if (sequence === this.renderSequence) this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItem extends HTMLElement {
  constructor() {
    super();

    this.removeItemButton = null;
    this.quantitySelectorInput = null;
  }

  connectedCallback() {
    this.removeItemButton = this.querySelector('[data-ref="remove-item"]');
    this.quantitySelectorInput = this.querySelector('[data-ref="quantity-selector-input"]');

    this.removeItemButton?.addEventListener('click', (event) => this.#handleItemRemove(event));
    this.quantitySelectorInput?.addEventListener('change', (event) => this.#handleQuantityInputChange(event));
  }

  disconnectedCallback() {
    this.quantitySelectorInput?.removeEventListener('change', (event) => this.#handleQuantityInputChange(event));
    this.removeItemButton?.removeEventListener('click', (event) => this.#handleItemRemove(event));
  }

  #handleItemRemove(event) {
    event.preventDefault();
    this.#updateLineItemQuantity(this.dataset.index, 0);
  }

  #handleQuantityInputChange(event) {
    event.preventDefault();
    this.#updateLineItemQuantity(this.dataset.index, this.quantitySelectorInput.value);
  }

  async #updateLineItemQuantity(line, quantity) {
    const drawer = this.closest('cart-drawer');
    if (drawer.dataset.updating === 'true') return;
    drawer.dataset.updating = 'true';
    drawer.classList.add('pd-cart-updating');
    this.#enableLoading();
    try {
      const root = window.Shopify?.routes?.root || '/';
      const currentResponse = await fetch(root + 'cart.js', { cache: 'no-store', credentials: 'same-origin' });
      if (!currentResponse.ok) throw new Error('Cart unavailable');
      const cart = await currentResponse.json();
      const item = cart.items.find(item => item.key === this.dataset.lineKey)
        || cart.items.find(item => String(item.variant_id) === this.dataset.variantId && String(item.properties?._pandrum_offre || '') === this.dataset.bundleId);
      if (!item) { await drawer.renderCartContents(); return; }
      const nextQuantity = Math.max(0, parseInt(quantity, 10) || 0);
      const bundle = item.properties?._pandrum_offre;
      const main = bundle && !item.properties?._pandrum_cadeau;
      // One atomic mutation keeps the handpan and every gift aligned for discount calculation.
      const updates = cart.items.map(current => {
        if (main && current.properties?._pandrum_offre === bundle) return nextQuantity;
        if (current.key === item.key) return nextQuantity;
        return current.quantity;
      });
      const response = await fetch(root + 'cart/update.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections: drawer.dataset.sectionId, sections_url: window.location.pathname }),
      });
      const result = await response.json();
      if (!response.ok || result.errors) throw new Error(typeof result.errors === 'string' ? result.errors : result.description || 'Cart update failed');
      document.dispatchEvent(new CartUpdatedEvent({}, { itemCount: result.item_count, source: 'cart-drawer-item', sections: result.sections }));
    } catch (error) {
      document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: error.message } }));
      await drawer.renderCartContents();
    } finally {
      drawer.dataset.updating = 'false';
      drawer.classList.remove('pd-cart-updating');
      this.#disableLoading();
    }
  }

  #enableLoading() {
    this.classList.add('cart-drawer__item--loading');
  }

  #disableLoading() {
    this.classList.remove('cart-drawer__item--loading');
  }
}

if (!customElements.get('cart-drawer-item')) {
  customElements.define('cart-drawer-item', CartDrawerItem);
}
